import { getAllCardConfigs } from "../nesting-summary-cards";
import { Injectable } from "@angular/core";
import type { NestingSummaryCardsComponent } from "../nesting-summary-cards.component";
import type { NestingCardsWorkflowDependencies } from "./nesting-cards-workflow-dependencies";

type NestingCardsSaveWorkflowContext = Pick<
  NestingSummaryCardsComponent,
  | "selectedCardConfig"
  | "tempSelectedCardConfig"
  | "nestGroupConfigsChange"
  | "originalNestGroupConfigsSnapshot"
  | "closeDialog"
  | "allTempChanges"
  | "originalStateBeforePopup"
  | "newNestGroupMapping"
  | "nestGroupConfigs"
  | "tempCardConfigs"
  | "hasUnsavedChanges"
  | "showUnsavedChangesDialog"
  | "totalCardConfigs"
  | "totalItems"
  | "filters"
  | "appliedFiltersState"
  | "showMoveSkuPopup"
  | "popupActiveNestGroup"
  | "total_nests"
  | "originalTotalNests"
>;
@Injectable({ providedIn: "root" })
export class NestingCardsSaveWorkflow {
  onPopUpSave(
    context: NestingCardsSaveWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
  ): void {
    if (!context.selectedCardConfig || !context.tempSelectedCardConfig) return;
    dependencies.saveTempChanges();

    dependencies.applyNewNestsFromTemp();
    dependencies.applyTempRowsToExistingCards();

    const { cluster_level, cluster_name } = dependencies.findFirstClusterInfo();
    const sku_update = dependencies.buildSkuUpdateFromTemp();

    context.nestGroupConfigsChange.emit({
      cluster_level,
      cluster_name,
      sku_update,
    } as any);

    context.originalNestGroupConfigsSnapshot = [];
    context.closeDialog();
  }
  applyNewNestsFromTemp(
    context: NestingCardsSaveWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    for (const [nestNumber, rows] of context.allTempChanges.entries()) {
      const isNewNest = !context.originalStateBeforePopup.has(nestNumber);
      if (!isNewNest) continue;
      const targetGroupValue = context.newNestGroupMapping.get(nestNumber);
      const targetGroupConfig = context.nestGroupConfigs.find(
        (ng) => ng.nestGroupValue === targetGroupValue,
      );
      if (!targetGroupConfig) continue;
      const newCardFromTemp = context.tempCardConfigs.find(
        (c) => c.header.nestNumber === nestNumber,
      );
      if (!newCardFromTemp) continue;
      targetGroupConfig.cardConfigs.push({
        ...newCardFromTemp,
        rows: rows.map((r) => ({ ...r })),
      });
    }
  }
  applyTempRowsToExistingCards(
    context: NestingCardsSaveWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    for (const group of context.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        const tempRows = context.allTempChanges.get(card.header.nestNumber);
        if (!tempRows) continue;
        const rowsChanged =
          card.rows.length !== tempRows.length ||
          JSON.stringify(card.rows.map((r) => r.sku)) !==
            JSON.stringify(tempRows.map((r) => r.sku));
        if (rowsChanged) card.rows = tempRows.map((r) => ({ ...r }));
      }
    }
  }
  findFirstClusterInfo(
    context: NestingCardsSaveWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): { cluster_level: string; cluster_name: string } {
    for (const group of context.nestGroupConfigs) {
      for (const card of group.cardConfigs) {
        if (
          card.rows.length > 0 &&
          card.rows[0].cluster_level &&
          card.rows[0].cluster_name
        ) {
          return {
            cluster_level: card.rows[0].cluster_level,
            cluster_name: card.rows[0].cluster_name,
          };
        }
      }
    }
    return { cluster_level: "", cluster_name: "" };
  }
  buildSkuUpdateFromTemp(
    context: NestingCardsSaveWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): Record<string, { is_new_nest: boolean; skus: string[] }> {
    const sku_update: Record<string, { is_new_nest: boolean; skus: string[] }> =
      {};
    for (const [nestNumber, currentRows] of context.allTempChanges.entries()) {
      const originalRows = context.originalStateBeforePopup.get(nestNumber);
      const isNewNest = !originalRows;
      let addedSkus: string[] = [];
      if (isNewNest) {
        addedSkus = currentRows
          .map((row) => row.sku)
          .filter((sku): sku is string => sku !== undefined && sku !== null);
      } else {
        const originalSkus = new Set(originalRows!.map((r) => r.sku));
        addedSkus = currentRows
          .filter((row) => !originalSkus.has(row.sku))
          .map((row) => row.sku)
          .filter((sku): sku is string => sku !== undefined && sku !== null);
      }
      if (addedSkus.length > 0) {
        sku_update[nestNumber.toString()] = {
          is_new_nest: isNewNest,
          skus: addedSkus,
        };
      }
    }
    return sku_update;
  }
  onMoveSkuDialogClose(
    context: NestingCardsSaveWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): void {
    if (context.hasUnsavedChanges()) {
      context.showUnsavedChangesDialog = true;
    } else {
      context.closeDialog();
    }
  }
  hasUnsavedChanges(
    context: NestingCardsSaveWorkflowContext,
    _dependencies: NestingCardsWorkflowDependencies,
  ): boolean {
    if (context.originalStateBeforePopup.size === 0) {
      return false;
    }
    for (const [
      nestNumber,
      originalRows,
    ] of context.originalStateBeforePopup.entries()) {
      const currentRows = context.allTempChanges.get(nestNumber);
      if (!currentRows) {
        return true;
      }
      if (originalRows.length !== currentRows.length) {
        return true;
      }
      for (let i = 0; i < originalRows.length; i++) {
        if (originalRows[i].sku !== currentRows[i].sku) {
          return true;
        }
      }
    }
    for (const nestNumber of context.allTempChanges.keys()) {
      if (!context.originalStateBeforePopup.has(nestNumber)) {
        return true;
      }
    }
    return false;
  }
  closeDialog(
    context: NestingCardsSaveWorkflowContext,
    dependencies: NestingCardsWorkflowDependencies,
  ): void {
    if (context.originalNestGroupConfigsSnapshot.length > 0) {
      for (let i = 0; i < context.nestGroupConfigs.length; i++) {
        const originalGroup = context.originalNestGroupConfigsSnapshot[i];
        if (originalGroup) {
          context.nestGroupConfigs[i].cardConfigs =
            originalGroup.cardConfigs.map((card) => ({
              ...card,
              header: { ...card.header },
              stats: card.stats.map((s) => ({ ...s })),
              columns: [...card.columns],
              rows: card.rows.map((r) => ({ ...r })),
            }));
        }
      }

      dependencies.setDefaultNestGroup();
      context.totalCardConfigs = getAllCardConfigs(context.nestGroupConfigs);
      context.totalItems = context.totalCardConfigs.length;
      context.showUnsavedChangesDialog = false;
    }
    for (const k of Object.keys(
      context.filters,
    ) as (keyof typeof context.filters)[]) {
      context.filters[k].selected = [];
      context.filters[k].search = "";
    }
    context.appliedFiltersState = {};
    context.showMoveSkuPopup = false;
    context.tempSelectedCardConfig = null;
    context.tempCardConfigs = [];
    context.selectedCardConfig = null;
    context.popupActiveNestGroup = null;
    context.allTempChanges.clear();
    context.originalStateBeforePopup.clear();
    context.originalNestGroupConfigsSnapshot = [];
    context.newNestGroupMapping.clear();
    context.total_nests = context.originalTotalNests;
    context.originalTotalNests = 0;
  }
}
